import React, { useRef, useEffect, useLayoutEffect, useState } from 'react';
import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import clsx from 'clsx';
import { Image } from './types';
import useColumns from './useColumns';
import useTableData from './useTableData';
import useDuplicateImagesStore from './useDuplicateImagesStore';
import { useSortedDuplicateImages } from './ImageMergeModal.utils';

interface DuplicateImagesTableProps {
  duplicateImages: Image[];
  newImage: Image;
}

type MetaType = { imageId?: number };

const DuplicateImagesTable = ({ duplicateImages, newImage }: DuplicateImagesTableProps) => {
  const [overlayStyle, setOverlayStyle] = useState<React.CSSProperties>({});
  const primaryHeaderRef = useRef(null);
  const containerRef = useRef(null);
  const primaryImageId = useDuplicateImagesStore(state => state.primaryImageId);
  const checkedImageIds = useDuplicateImagesStore(state => state.checkedImageIds);
  const isImageChecked = useDuplicateImagesStore(state => state.isImageChecked);
  const setPrimaryImage = useDuplicateImagesStore(state => state.setPrimaryImage);
  const toggleImageChecked = useDuplicateImagesStore(state => state.toggleImageChecked);
  const resetStore = useDuplicateImagesStore(state => state.reset);

  const sortedDuplicateImages = useSortedDuplicateImages(duplicateImages);
  const columns = useColumns(newImage, sortedDuplicateImages, [primaryImageId, checkedImageIds]);
  const tableData = useTableData(newImage, sortedDuplicateImages);
  const table = useReactTable({
    columns,
    data: tableData,
    getCoreRowModel: getCoreRowModel(),
  });

  const headerRows = table.getRowModel().rows.filter(row => row.original.header === 'imageData');
  const bodyRows = table.getRowModel().rows.filter(row => row.original.header !== 'imageData');

  useLayoutEffect(() => {
    if (!containerRef.current) return;
    if (!primaryHeaderRef.current) return;
    if (!primaryImageId) return;

    const headerRect = primaryHeaderRef.current.getBoundingClientRect();
    const containerRect = containerRef.current.getBoundingClientRect();

    setTimeout(() => {
      if (!headerRect) return;
      if (!containerRect) return;
      if (!containerRef.current) return;

      setOverlayStyle({
        height: containerRect.height - 4,
        left: (headerRect.left - containerRect.left) + containerRef.current.scrollLeft,
        top: 0,
        width: headerRect.width - 4,
      });
    }, 50);
  }, [primaryImageId, table, primaryHeaderRef.current, containerRef.current]);

  useEffect(() => {
    resetStore();
    setPrimaryImage(newImage.id);
    sortedDuplicateImages.forEach(image => toggleImageChecked(image.id));
  }, []);

  return (
    <div className="tw-text-sm tw-w-full tw-overflow-y-auto tw-min-h-0 tw-mt-3">
      <div
        ref={containerRef}
        className={clsx(
          'tw-overflow-hidden tw-overflow-x-auto tw-relative',
          'tw-rounded-lg tw-outline tw-outline-med-grey tw-outline-1',
        )}
      >
        <table
          className="tw-table-fixed tw-border-collapse"
          style={{ width: `${(sortedDuplicateImages.length + 2) * 160}px` }}
        >
          <thead>
            {headerRows.map(row => (
              <tr key={`${row.original.header}-${row.id}`}>
                {row.getVisibleCells().map((cell) => {
                  const meta = (cell.column.columnDef.meta || {}) as MetaType;
                  const imageId = meta?.imageId || null;
                  const isPrimary = !!primaryImageId && primaryImageId === imageId;

                  return (
                    <th
                      key={cell.id}
                      ref={isPrimary ? primaryHeaderRef : null}
                      className={clsx(
                        'tw-max-w-40 tw-min-w-40 tw-w-40',
                        'tw-px-2 tw-py-1 tw-bg-light-grey tw-align-middle',
                        'tw-border tw-border-solid tw-border-med-grey tw-border-1 tw-relative',
                        { 'tw-opacity-60': imageId && !isImageChecked(imageId) },
                      )}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {bodyRows.map(row => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => {
                  const meta = (cell.column.columnDef.meta || {}) as MetaType;
                  const imageId = meta?.imageId || null;

                  return (
                    <td
                      key={cell.id}
                      className={clsx(
                        'tw-max-w-40 tw-min-w-40 tw-w-40',
                        'tw-px-2 tw-py-1 tw-align-middle tw-break-words',
                        'tw-border tw-border-solid tw-border-med-grey tw-border-1',
                        {
                          'tw-bg-light-grey': cell.column.id === 'header',
                          'tw-bg-white': cell.column.id !== 'header',
                          'tw-opacity-60': imageId && !isImageChecked(imageId),
                        },
                      )}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>

        {primaryImageId && (
          <div
            className={clsx(
              'tw-absolute tw-pointer-events-none tw-bg-tci-purple tw-bg-opacity-10',
              'tw-border-2 tw-border-tci-purple tw-border-solid tw-rounded-md',
            )}
            style={overlayStyle}
          />
        )}
      </div>
    </div>
  );
};

export default DuplicateImagesTable;
